<template>
  <layout class="booking-table">
    <page-header :actions="actions">
      <template slot="header">
        <h4 class="mr-4">
          <i class="icon-stack mr-2"></i>
          {{ $t('bookings.title') }}
        </h4>
        <b-nav pills class="mb-0">
          <b-nav-item
            v-for="(tab, key) in tabsVisible"
            :key="key"
            :active="key === currentTab"
            link-classes="py-1"
            @click="setTab(key)"
          >
            {{ tab.label }}
            <b-badge v-if="tab.count !== null" variant="light">
              {{ tab.count }}
            </b-badge>
          </b-nav-item>
        </b-nav>
      </template>
    </page-header>
    <div class="content">
      <admin-table ref="table" v-bind="tableProps" @api-result="apiResult">
        <template slot="route" slot-scope="{ item }">
          <booking-route :booking="item" />
        </template>
        <template slot="payment" slot-scope="{ item }">
          <b-badge variant="info" class="mr-2">
            {{ getOrder(item).status_label }}
          </b-badge>
          <value-payment-method :item="getOrder(item)" />
        </template>
        <template slot="vehicle" slot-scope="{ item }">
          <img
            v-if="item.vehicle.image && item.vehicle.image.url"
            :src="item.vehicle.image.url"
            class="w-32 max-w-full mr-3"
          />
          <div class="text-xs">{{ item.vehicle.name }}</div>
          <booking-amount :booking="item" />
        </template>
      </admin-table>
    </div>
  </layout>
</template>

<script>
import { AdminTable } from 'stylemix-vue-admin'
import BookingsApi from './Api'
import BookingsIndexMixin from '../../../console/src/bookings/BookingsIndexMixin'
import BookingRoute from '../../../console/src/bookings/BookingRoute'

const resourceBase = 'bookings'

export default {
  name: 'Bookings',

  components: {
    AdminTable,
    BookingRoute,
  },

  mixins: [BookingsIndexMixin],

  data() {
    return {
      // Page header actions
      actions: [
        {
          id: 'create',
          text: this.$t('bookings.new'),
          icon: 'icon-plus-circle2',
          onClick() {
            this.$router.push({
              name: `${resourceBase}.create`,
            })
          },
        },
      ],

      // Table resource configuration
      tableProps: {
        base: resourceBase,
        apiBuilder: () => {
          return new BookingsApi({
            context: 'list',
            include:
              'vehicle,vehicle.media,returnBooking,order,returnBooking.order,origin,destination',
            with: 'totals',
            'where[is_return]': false,
            'where[status]': this.tabs[this.currentTab].status,
          })
        },
        sortBy: 'ordered_at',
        sortDesc: false,
        perPage: 30,
        columns: [
          {
            key: 'id',
            label: this.$t('general.id'),
            sortable: true,
          },
          {
            key: 'created_at',
            label: this.$t('general.date'),
            sortable: true,
            component: 'value-date',
            config: {
              stacked: true,
            },
          },
          {
            key: 'route',
            label: this.$t('bookings.route'),
            tdClass: 'text-sm',
          },
          {
            key: 'ordered_at',
            label: this.$t('bookings.pickup_date'),
            sortable: true,
            component: 'value-date',
            config: {
              stacked: true,
              zone: true,
              zoneAttribute: 'timezone',
            },
          },
          {
            key: 'payment',
            label: this.$t('general.payment'),
          },
          {
            key: 'vehicle',
            label: this.$t('bookings.vehicle_class'),
          },
        ],

        searchFields: [
          {
            component: 'text-field',
            type: 'search',
            attribute: 'query',
            placeholder: this.$t('general.search'),
          },
          {
            component: 'text-field',
            attribute: 'where.number',
            placeholder: this.$t('bookings.number'),
          },
          {
            component: 'date-range-field',
            attribute: 'where.ordered_at',
            placeholder: this.$t('general.select_period'),
          },
        ],

        rowActions: (/*item*/) => {
          return ['edit']
        },

        bulkActions() {
          return []
        },
      },

      currentTab: null,

      tabs: {
        active: {
          label: this.$t('enums.booking_status.active'),
          status: 'pending,assigned,rejected,processing,draft',
          count: null,
        },
        assigned: {
          label: this.$t('enums.booking_status.assigned'),
          status: 'assigned',
          count: null,
        },
        rejected: {
          label: this.$t('enums.booking_status.rejected'),
          status: 'rejected',
          count: null,
        },
        cancelled: {
          label: this.$t('enums.booking_status.cancelled'),
          status: 'cancelled',
          count: null,
        },
        complete: {
          label: this.$t('enums.booking_status.complete'),
          status: 'complete',
          count: null,
        },
      },
    }
  },

  computed: {
    tabsVisible() {
      const tabs = this.tabs

      return {
        active: tabs.active,
        complete: tabs.complete,
        cancelled: tabs.cancelled,
      }
    },
  },

  created() {
    this.currentTab = Object.keys(this.tabsVisible)[0]
  },

  methods: {
    apiResult(result) {
      Object.keys(this.tabs).forEach(tab => {
        this.tabs[tab].count = 0
        if (tab === 'active' && result.totals) {
          const possible = [
            'pending',
            'processing',
            'assigned',
            'rejected',
            'draft',
          ]
          this.tabs[tab].count = possible.reduce(
            (acc, curr) => acc + (result.totals[curr] || 0),
            0,
          )
        } else if (result.totals && result.totals[tab]) {
          this.tabs[tab].count = result.totals[tab]
        }
      })
    },

    setTab(key) {
      this.currentTab = key
      this.$refs.table.reset()
    },

    assign(booking) {
      this.assignDialog(booking).then(assigned => {
        // Empty argument will mean that nothing is changed
        if (!assigned) {
          return
        }

        this.$refs.table.refresh()
      })
    },
  },
}
</script>
